import React, { useEffect, useRef, useState } from 'react';
import { CheckOutlined } from '@ant-design/icons';

// Lightweight custom dropdown for the Inbox header (filter / options menus).
// antd's Dropdown/Menu pull in useBreakpoint -> matchMedia, which isn't
// available in the vitest/jsdom test environment, so this is a plain
// absolutely-positioned <div> that closes on outside click or selection.

export interface InboxMenuOption<T extends string> {
  value: T;
  label: string;
  icon?: React.ReactNode;
}

export interface InboxMenuProps<T extends string> {
  triggerLabel: string;
  triggerIcon: React.ReactNode;
  options: InboxMenuOption<T>[];
  selected?: T;
  onSelect: (value: T) => void;
  onReset?: () => void;
  align?: 'left' | 'right';
  triggerActive?: boolean;
}

export function InboxMenu<T extends string>({
  triggerLabel,
  triggerIcon,
  options,
  selected,
  onSelect,
  onReset,
  align = 'right',
  triggerActive = false,
}: InboxMenuProps<T>) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleOutsideClick = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [open]);

  return (
    <div className="relative" ref={containerRef}>
      <div
        onClick={() => setOpen((o) => !o)}
        title={triggerLabel}
        aria-label={triggerLabel}
        className={`flex items-center justify-center w-6 h-6 rounded cursor-pointer ${
          triggerActive ? 'bg-blue-100 text-blue-600' : 'hover:bg-neutral-200 text-neutral-900'
        }`}
      >
        {triggerIcon}
      </div>
      {open && (
        <div
          className={`absolute top-full mt-1 z-50 flex min-w-[180px] flex-col gap-2 rounded-md border border-solid border-neutral-300 bg-brand-white p-2 shadow-md ${
            align === 'right' ? 'right-0' : 'left-0'
          }`}
        >
          <div className="flex w-full flex-col">
            {options.map((option) => {
              const isSelected = selected === option.value;
              return (
                <div
                  key={option.value}
                  onClick={() => {
                    onSelect(option.value);
                    setOpen(false);
                  }}
                  className={`flex items-center justify-between gap-2 rounded px-2 py-1 text-sm cursor-pointer whitespace-nowrap ${
                    isSelected
                      ? 'border border-solid border-blue-200 bg-blue-100 text-blue-600'
                      : 'text-neutral-900 hover:bg-neutral-200'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    {option.icon && (
                      <span className="inline-flex w-4 shrink-0 items-center justify-center text-neutral-900">
                        {option.icon}
                      </span>
                    )}
                    {option.label}
                  </span>
                  {isSelected && <CheckOutlined className="shrink-0 text-blue-600" />}
                </div>
              );
            })}
          </div>
          {onReset && (
            <div
              role="button"
              onClick={() => {
                onReset();
                setOpen(false);
              }}
              className="self-end cursor-pointer rounded px-2 py-1 text-sm font-medium text-neutral-900 whitespace-nowrap hover:bg-neutral-200"
            >
              Reset Filter
            </div>
          )}
        </div>
      )}
    </div>
  );
}
